// src/components/risk/RiskScoreBreakdown.jsx
// Detailed breakdown of risk score factors with category weights

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Info, TrendingUp, TrendingDown, Minus, CheckCircle } from 'lucide-react';
import { CATEGORY_WEIGHTS, getRiskCategory } from './constants';

// Factors that cannot be manually resolved (system-detected or baseline)
const SYSTEM_FACTORS = [
  'no_blue_button_baseline',
  'blue_button_connected',
  'baseline_solicitation',
  'baseline solicitation',
  'new_client_baseline',
  'new client',
  '90 day baseline',
  'baseline market risk',
  'loyalty_tier',
  'pulse_protection',
  'referral_loyalty',
  'client_initiated',
  'tenure_discount'
];

// Check if a factor can be manually resolved by agent
const canResolve = (factor) => {
  const code = (factor.code || '').toLowerCase();
  const name = (factor.name || '').toLowerCase();

  // System factors cannot be resolved
  if (SYSTEM_FACTORS.some(sf => code.includes(sf) || name.includes(sf))) {
    return false;
  }
  // Credits, overrides, and baselines cannot be resolved
  if (factor.isCredit || factor.isOverride || factor.isBaseline) {
    return false;
  }
  // Factors with 0 or negative points don't need resolving
  if (factor.points <= 0) {
    return false;
  }
  return true;
};

// Get the styling class for a factor based on its type
const getFactorStyle = (factor) => {
  if (factor.isBaseline) {
    return { bg: 'bg-amber-900/20 border border-amber-500/30', text: 'text-amber-300', pointsText: 'text-amber-400', icon: 'text-amber-400' };
  }
  if (factor.isCredit) {
    return { bg: 'bg-green-900/20 border border-green-500/30', text: 'text-green-300', pointsText: 'text-green-400', icon: 'text-green-400' };
  }
  if (factor.isOverride) {
    return { bg: 'bg-blue-900/20 border border-blue-500/30', text: 'text-blue-300', pointsText: 'text-blue-400', icon: 'text-blue-400' };
  }
  if (factor.isPenalty) {
    return { bg: 'bg-red-900/20 border border-red-500/30', text: 'text-red-300', pointsText: 'text-red-400', icon: 'text-red-400' };
  }
  // Default - based on points
  if (factor.points > 0) {
    return { bg: 'bg-slate-900/30', text: 'text-slate-300', pointsText: 'text-red-400', icon: 'text-red-400' };
  }
  if (factor.points < 0) {
    return { bg: 'bg-slate-900/30', text: 'text-slate-300', pointsText: 'text-green-400', icon: 'text-green-400' };
  }
  return { bg: 'bg-slate-900/30', text: 'text-slate-300', pointsText: 'text-slate-500', icon: 'text-slate-500' };
};

const RiskScoreBreakdown = ({
  score = 0,
  factors = [],
  categoryBreakdown = {},
  autoTrigger = null,
  onResolveFactor = null,
  clientId = null,
  className = ''
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  // Group factors by category
  const factorsByCategory = factors.reduce((acc, factor) => {
    const cat = factor.category || 'unknown';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(factor);
    return acc;
  }, {});

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Map camelCase to snake_case for category keys
  const keyMap = {
    engagement: 'engagement',
    utilization: 'utilization',
    benefit_fit: 'benefitFit',
    life_events: 'lifeEvents',
    external: 'external'
  };

  // Calculate total possible and actual weighted contribution per category
  const getCategoryContribution = (catKey) => {
    const weight = CATEGORY_WEIGHTS[catKey]?.weight || 0;
    const apiKey = keyMap[catKey] || catKey;

    // Handle both object format {normalizedScore, weightedScore} and plain number
    const catData = categoryBreakdown[apiKey] || categoryBreakdown[catKey] || 0;
    const rawScore = typeof catData === 'object'
      ? (catData.normalizedScore ?? catData.weightedScore ?? 0)
      : (Number(catData) || 0);
    // Check for null/undefined explicitly since isNaN(null) returns false
    const weightedScore = typeof catData === 'object' && catData.weightedScore != null
      ? catData.weightedScore
      : rawScore * weight;

    return {
      weight: weight * 100,
      rawScore: isNaN(rawScore) || rawScore == null ? 0 : rawScore,
      weightedScore: isNaN(weightedScore) || weightedScore == null ? 0 : weightedScore,
      maxPossible: 100 * weight
    };
  };

  return (
    <div className={`${className}`}>
      {/* Auto-trigger warning if score is 100 */}
      {autoTrigger && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-red-400">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">Auto-Flagged: {autoTrigger}</span>
          </div>
        </div>
      )}

      {/* Overall score summary */}
      <div className="mb-4 p-4 bg-slate-900/50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-sm">Total Risk Score</span>
          <span
            className="text-2xl font-bold"
            style={{ color: getRiskCategory(score).color }}
          >
            {Math.round(score)}
          </span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(100, score)}%`,
              backgroundColor: getRiskCategory(score).color
            }}
          />
        </div>
      </div>

      {/* Category breakdown */}
      <div className="space-y-3">
        {Object.entries(CATEGORY_WEIGHTS).map(([catKey, config]) => {
          const contribution = getCategoryContribution(catKey);
          const categoryFactors = factorsByCategory[catKey] || [];
          const isExpanded = expandedCategories[catKey];
          const hasFactors = categoryFactors.length > 0;

          return (
            <div
              key={catKey}
              className={`border rounded-lg overflow-hidden ${config.bgColor} border-slate-600/30`}
            >
              {/* Category header */}
              <button
                onClick={() => hasFactors && toggleCategory(catKey)}
                className={`w-full px-4 py-3 flex items-center justify-between ${
                  hasFactors ? 'cursor-pointer hover:bg-slate-700/20' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-3">
                  {hasFactors ? (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )
                  ) : (
                    <div className="w-4" />
                  )}
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />
                  <div className="text-left">
                    <div className="text-white font-medium">{config.label}</div>
                    <div className="text-xs text-slate-400">{config.description}</div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Weight indicator */}
                  <div className="text-right">
                    <div className="text-xs text-slate-500">{contribution.weight.toFixed(0)}% weight</div>
                    <div className="text-sm font-medium" style={{ color: config.color }}>
                      +{contribution.weightedScore.toFixed(1)} pts
                    </div>
                  </div>
                </div>
              </button>

              {/* Factor progress bar */}
              <div className="px-4 pb-3">
                <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(100, (contribution.weightedScore / contribution.maxPossible) * 100)}%`,
                      backgroundColor: config.color
                    }}
                  />
                </div>
              </div>

              {/* Expanded factor details */}
              {isExpanded && hasFactors && (
                <div className="px-4 pb-3 border-t border-slate-600/30">
                  <div className="mt-3 space-y-2">
                    {categoryFactors.map((factor, idx) => {
                      const style = getFactorStyle(factor);
                      const showDescription = factor.isBaseline || factor.isCredit || factor.isOverride || factor.isPenalty;
                      return (
                        <div
                          key={idx}
                          className={`py-1.5 px-2 rounded ${style.bg}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {factor.isBaseline || factor.isOverride ? (
                                <Info className={`w-3 h-3 ${style.icon} flex-shrink-0`} />
                              ) : factor.points > 0 ? (
                                <TrendingUp className={`w-3 h-3 ${style.icon} flex-shrink-0`} />
                              ) : factor.points < 0 ? (
                                <TrendingDown className={`w-3 h-3 ${style.icon} flex-shrink-0`} />
                              ) : (
                                <Minus className="w-3 h-3 text-slate-500 flex-shrink-0" />
                              )}
                              <span className={`text-sm truncate ${style.text}`}>{factor.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-sm font-medium ${style.pointsText}`}>
                                {factor.points > 0 ? '+' : ''}{factor.points}
                              </span>
                              {canResolve(factor) && onResolveFactor && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onResolveFactor(factor, clientId);
                                  }}
                                  className="px-2 py-0.5 text-xs bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded transition-colors"
                                  title="Click to mark this factor as resolved"
                                >
                                  <CheckCircle className="w-3 h-3 inline mr-1" />
                                  Resolve
                                </button>
                              )}
                            </div>
                          </div>
                          {showDescription && factor.description && (
                            <p className={`text-xs ${style.pointsText} opacity-70 mt-1 ml-5`}>{factor.description}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* All factors summary */}
      {factors.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <h4 className="text-sm font-medium text-slate-400 mb-2">All Active Factors ({factors.length})</h4>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {factors
              .sort((a, b) => Math.abs(b.points) - Math.abs(a.points))
              .map((factor, idx) => {
                const style = getFactorStyle(factor);
                const isSpecial = factor.isBaseline || factor.isCredit || factor.isOverride || factor.isPenalty;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between text-xs py-1.5 px-2 rounded ${
                      isSpecial ? style.bg : 'hover:bg-slate-700/30'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      {(factor.isBaseline || factor.isOverride) && <Info className={`w-3 h-3 ${style.icon} flex-shrink-0`} />}
                      {factor.isCredit && <TrendingDown className={`w-3 h-3 ${style.icon} flex-shrink-0`} />}
                      {factor.isPenalty && <TrendingUp className={`w-3 h-3 ${style.icon} flex-shrink-0`} />}
                      <span className={`truncate mr-2 ${isSpecial ? style.text : 'text-slate-300'}`}>{factor.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`font-medium whitespace-nowrap ${style.pointsText}`}>
                        {factor.points > 0 ? '+' : ''}{factor.points} pts
                      </span>
                      {canResolve(factor) && onResolveFactor && (
                        <button
                          onClick={() => onResolveFactor(factor, clientId)}
                          className="px-2 py-0.5 text-xs bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded transition-colors whitespace-nowrap"
                          title="Click to mark this factor as resolved"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskScoreBreakdown;
